// `Element.textContent` and the regex-tag-strip fallback both concatenate
// nested text nodes without inserting any whitespace, so adjacent block
// elements collapse together: `<p>x</p><p>y</p>` would become `xy`, and
// `<ul><li>First</li><li>Second</li></ul>` would become `FirstSecond`. Insert
// a space immediately after every block-closing tag (and in place of `<br>`)
// before extracting text so paragraphs and list items stay readable in
// spreadsheet cells.
const BLOCK_CLOSE_TAG = /<\/(p|li|ul|ol|h[1-6]|div|tr)>/gi;
const BR_TAG = /<br\s*\/?>/gi;

const insertBlockBoundaries = (html: string): string =>
  html.replace(BLOCK_CLOSE_TAG, '$& ').replace(BR_TAG, ' ');

/**
 * Strip HTML markup from a note's `content` so it renders cleanly in plain
 * surfaces (spreadsheet cells, "most recent note" labels) that have no HTML
 * renderer. Legacy plain-text notes (no tags) round-trip unchanged.
 *
 * Implementation note: relies on the browser's HTML parser via a transient
 * `<div>`. Safe because nothing executes — the markup is parsed for text
 * extraction only and the element is never attached to the document.
 */
export const noteContentToPlainText = (content: string): string => {
  if (!content) {
    return '';
  }
  const withBoundaries = insertBlockBoundaries(content);
  if (typeof document === 'undefined') {
    // Bare-text fallback for non-browser environments (SSR, Node-side tests).
    return withBoundaries
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
  const div = document.createElement('div');
  div.innerHTML = withBoundaries;
  return (div.textContent ?? '').replace(/\s+/g, ' ').trim();
};
